import React from 'react';
import { ArrowRight } from 'lucide-react';
import { compactText, formatLegalDate, getHealthTone } from '@/lib/legalOs';

const surfaceStyle = {
  border: '1px solid var(--border-subtle)',
  background: 'linear-gradient(180deg, color-mix(in srgb, var(--bg-elevated) 90%, white 10%), var(--bg-surface))',
  borderRadius: '24px',
  padding: '18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  minWidth: 0,
};

function countLabel(count) {
  return count > 99 ? '99+' : String(count || 0);
}

function ActionBadge({ children, tone = 'default' }) {
  const styles = tone === 'success'
    ? {
      background: 'rgba(5, 150, 105, 0.12)',
      color: '#047857',
      border: '1px solid rgba(5, 150, 105, 0.2)',
    }
    : tone === 'quiet'
      ? {
        background: 'var(--bg-elevated)',
        color: 'var(--text-secondary)',
        border: '1px solid var(--border-subtle)',
      }
      : {
        background: 'rgba(15, 118, 110, 0.1)',
        color: 'var(--accent-primary)',
        border: '1px solid rgba(15, 118, 110, 0.16)',
      };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        ...styles,
      }}
    >
      {children}
    </span>
  );
}

export function MatterHomeSection({
  title,
  count,
  action = null,
  children,
  dataTestId,
}) {
  return (
    <section style={surfaceStyle} data-testid={dataTestId}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '4px' }}>
          <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', lineHeight: 1.2 }}>
            {title}
          </h2>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
            {countLabel(count)}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function MatterContinueCard({
  matter,
  onContinue,
  dataTestId,
}) {
  const healthTone = getHealthTone(matter?.health_score || 0);
  const hasHearing = Boolean(matter?.next_hearing_date);

  return (
    <button
      type="button"
      onClick={onContinue}
      data-testid={dataTestId}
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        borderRadius: '22px',
        padding: '16px',
        textAlign: 'left',
        display: 'grid',
        gap: '14px',
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ActionBadge tone={matter?.has_resume ? 'success' : 'default'}>
              {matter?.has_resume ? 'Resume ready' : 'Matter'}
            </ActionBadge>
            {matter?.continue_kind ? (
              <ActionBadge tone="quiet">
                {matter.continue_kind}
              </ActionBadge>
            ) : null}
          </div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', lineHeight: 1.25 }}>
            {matter?.title || 'Matter'}
          </h3>
        </div>
        {(matter?.health_score || 0) > 0 ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '7px 10px',
              borderRadius: '999px',
              fontSize: '11px',
              fontWeight: 700,
              border: `1px solid ${healthTone.border}`,
              background: healthTone.background,
              color: healthTone.color,
              whiteSpace: 'nowrap',
            }}
          >
            {healthTone.label}
          </span>
        ) : null}
      </div>

      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
        {compactText(matter?.continue_summary || matter?.summary || 'Continue this matter.', 110)}
      </p>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '12px' }}>
        {matter?.court_name ? <span>{matter.court_name}</span> : null}
        {matter?.case_number ? <span>{matter.case_number}</span> : null}
        {matter?.stage ? <span>{matter.stage}</span> : null}
        {hasHearing ? <span>Hearing {formatLegalDate(matter.next_hearing_date)}</span> : null}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
          {matter?.continue_source || 'Matter desk'}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            color: 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: 800,
          }}
        >
          {matter?.continue_label || 'Continue'}
          <ArrowRight size={14} />
        </span>
      </div>
    </button>
  );
}

export function MatterCompactAction({
  item,
  onOpen,
  dataTestId,
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={dataTestId}
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        borderRadius: '18px',
        padding: '14px 15px',
        textAlign: 'left',
        display: 'grid',
        gap: '8px',
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>
        <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, minWidth: 0 }}>
          {item?.title || 'Untitled'}
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '11px', whiteSpace: 'nowrap' }}>
          {item?.continue_kind || item?.kind || item?.stage || ''}
        </span>
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.55 }}>
        {compactText(item?.continue_summary || item?.summary || 'Continue this work.', 95)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
          {item?.continue_source || item?.court_name || item?.updated_at || ''}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', color: 'var(--text-primary)', fontSize: '12px', fontWeight: 700 }}>
          {item?.continue_label || item?.cta || 'Continue'}
          <ArrowRight size={13} />
        </span>
      </div>
    </button>
  );
}

export function MatterPrimaryAction({
  eyebrow,
  title,
  summary,
  cta,
  onClick,
  secondaryAction = null,
  dataTestId,
}) {
  return (
    <section
      style={{
        ...surfaceStyle,
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--bg-elevated) 88%, var(--accent-primary) 8%), var(--bg-surface))',
      }}
      data-testid={dataTestId}
    >
      <div style={{ display: 'grid', gap: '10px' }}>
        {eyebrow ? (
          <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {eyebrow}
          </span>
        ) : null}
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '24px', lineHeight: 1.15 }}>
          {title}
        </h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.65 }}>
          {summary}
        </p>
      </div>

      <button
        type="button"
        onClick={onClick}
        style={{
          border: 'none',
          borderRadius: '16px',
          padding: '14px 16px',
          background: 'var(--text-primary)',
          color: 'var(--bg-surface)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        {cta}
        <ArrowRight size={15} />
      </button>

      {secondaryAction}
    </section>
  );
}
